import { useEffect, useMemo, useState } from 'react';
import { apiRequest } from '@/lib/api';

type LieuReservationTransaction = {
  id: number;
  lieu_location_id: number;
  lieu_nom?: string | null;
  lieu_ville?: string | null;
  nom: string;
  email: string;
  telephone?: string | null;
  adresse?: string | null;
  date_reservation: string;
  creneau_id?: string | null;
  creneau_label: string;
  montant: number;
  message?: string | null;
  statut: 'paiement_en_attente' | 'envoyee' | 'confirmee' | 'refusee' | 'annulee' | string;
  status_paiement?: string | null;
  payment_method?: string | null;
  checkout_url?: string | null;
  transaction_id?: string | null;
  created_at?: string;
};

const formatMoney = (value: number) => `${Math.round(value).toLocaleString('fr-FR')} FCFA`;
const formatDate = (value?: string) =>
  value ? new Date(value).toLocaleString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Non renseigné';
const formatReservationDate = (value?: string) =>
  value ? new Date(`${value}T12:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Non renseigné';
const DEXPAY_RATE = 0.02;
const FSN_COMMISSION_FEE = 500;

const statusLabel = (value: string) => {
  if (value === 'completed') return 'Paiement réussi';
  if (value === 'pending') return 'Paiement non validé';
  if (value === 'failed') return 'Échoué';
  if (value === 'cancelled') return 'Annulé';
  if (value === 'paiement_en_attente') return 'Paiement non validé';
  if (value === 'envoyee') return 'Envoyée';
  if (value === 'confirmee') return 'Confirmée';
  if (value === 'refusee') return 'Refusée';
  if (value === 'annulee') return 'Annulée';
  return value || 'Non renseigné';
};

const statusClass = (value?: string | null) => {
  if (value === 'completed' || value === 'confirmee') return 'bg-emerald-100 text-emerald-700';
  if (value === 'failed' || value === 'refusee' || value === 'annulee' || value === 'cancelled') return 'bg-red-100 text-red-700';
  return 'bg-amber-100 text-amber-700';
};

const paymentMethodLabel = (value?: string | null) => {
  if (!value) return 'Non renseigné';
  if (value === 'dexpay') return 'DexPay';
  if (value === 'wave') return 'Wave';
  if (value === 'orange_money') return 'Orange Money';
  if (value === 'mixx_by_yas') return 'Mixx by Yas';
  return value.replace(/_/g, ' ');
};

const estimateDexpayFee = (total: number) => Math.ceil(Math.max(0, Number(total) || 0) * DEXPAY_RATE);
const estimateFsnCommission = (transaction: LieuReservationTransaction) =>
  transaction.creneau_id === 'visite_gratuite' || Number(transaction.montant ?? 0) <= 0 ? 0 : FSN_COMMISSION_FEE;

export default function AdminLieuFinance() {
  const [transactions, setTransactions] = useState<LieuReservationTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'completed' | 'pending' | 'failed'>('all');
  const [kindFilter, setKindFilter] = useState<'all' | 'reservation' | 'visit'>('all');
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<LieuReservationTransaction | null>(null);
  const completedTransactions = useMemo(
    () => transactions.filter((transaction) => transaction.status_paiement === 'completed'),
    [transactions]
  );

  useEffect(() => {
    const loadTransactions = async () => {
      setLoading(true);
      try {
        const response = await apiRequest<LieuReservationTransaction[]>('/admin/lieux-locations/reservations');
        setTransactions(response);
        setError('');
      } catch (requestError) {
        console.error('Admin location finance error:', requestError);
        setError(requestError instanceof Error ? requestError.message : 'Impossible de charger la finance des salles.');
      } finally {
        setLoading(false);
      }
    };

    void loadTransactions();
  }, []);

  const handleDelete = async (transaction: LieuReservationTransaction) => {
    if (!window.confirm(`Supprimer la transaction LOC-${transaction.id} ?`)) {
      return;
    }

    try {
      await apiRequest(`/admin/reservations-lieux/${transaction.id}`, { method: 'DELETE' });
      setTransactions((current) => current.filter((item) => item.id !== transaction.id));
      setSelectedTransaction((current) => current?.id === transaction.id ? null : current);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Impossible de supprimer cette transaction.');
    }
  };

  const filteredTransactions = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return transactions.filter((transaction) => {
      const isVisit = transaction.creneau_id === 'visite_gratuite';
      const rawStatus = transaction.status_paiement || transaction.statut;
      const normalizedStatus = rawStatus === 'completed' || rawStatus === 'confirmee'
        ? 'completed'
        : rawStatus === 'failed' || rawStatus === 'refusee' || rawStatus === 'annulee' || rawStatus === 'cancelled'
          ? 'failed'
          : 'pending';

      const matchesKind = kindFilter === 'all' || (kindFilter === 'visit' ? isVisit : !isVisit);
      const matchesStatus = statusFilter === 'all' || normalizedStatus === statusFilter;
      const matchesSearch = !normalizedSearch
        || String(transaction.id).includes(normalizedSearch)
        || (transaction.lieu_nom ?? '').toLowerCase().includes(normalizedSearch)
        || transaction.nom.toLowerCase().includes(normalizedSearch)
        || transaction.email.toLowerCase().includes(normalizedSearch);

      return matchesKind && matchesStatus && matchesSearch;
    });
  }, [kindFilter, search, statusFilter, transactions]);

  const dexpayFees = completedTransactions.reduce((sum, transaction) => sum + estimateDexpayFee(Number(transaction.montant ?? 0)), 0);
  const fsnCommissions = completedTransactions.reduce((sum, transaction) => sum + estimateFsnCommission(transaction), 0);
  const pendingTransactions = transactions.filter((transaction) => {
    const status = transaction.status_paiement || transaction.statut;
    return status !== 'completed' && status !== 'confirmee';
  });

  const stats = [
    { label: 'Revenus salles', value: formatMoney(completedTransactions.reduce((sum, transaction) => sum + Number(transaction.montant ?? 0), 0)), icon: 'ri-community-line', color: 'bg-gold-100 text-gold-dark' },
    { label: 'Commissions FSN', value: formatMoney(fsnCommissions), icon: 'ri-percent-line', color: 'bg-emerald-100 text-emerald-700' },
    { label: 'Frais DexPay (2%)', value: formatMoney(dexpayFees), icon: 'ri-percent-line', color: 'bg-blue-100 text-blue-700' },
    { label: 'Transactions payées', value: String(completedTransactions.length), icon: 'ri-exchange-dollar-line', color: 'bg-gold-100 text-gold-dark' },
    { label: 'Paiements non validés', value: String(pendingTransactions.length), icon: 'ri-error-warning-line', color: 'bg-amber-100 text-amber-700' },
  ];

  if (loading) {
    return <div className="rounded-xl border border-gray-100 bg-white p-6 text-sm text-gray-400">Chargement de la finance des salles...</div>;
  }

  if (error) {
    return <div className="rounded-xl border border-red-100 bg-red-50 p-6 text-sm text-red-600">{error}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
        {stats.map((card) => (
          <div key={card.label} className="rounded-2xl border border-[#eef1f5] bg-white p-5 shadow-[0_8px_24px_rgba(15,23,42,0.04)]">
            <div className={`mb-4 flex h-12 w-12 items-center justify-center rounded-2xl ${card.color}`}>
              <i className={`${card.icon} text-lg`}></i>
            </div>
            <p className="text-sm text-gray-500">{card.label}</p>
            <p className="mt-1 text-2xl font-bold text-navy">{card.value}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-2xl border border-[#eef1f5] bg-white shadow-[0_10px_30px_rgba(15,23,42,0.04)]">
        <div className="flex flex-col gap-3 border-b border-[#eef1f5] px-5 py-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gold-100">
              <i className="ri-building-2-line text-gold-dark text-sm"></i>
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900">Paiements salles & visites</h3>
              <p className="text-xs text-gray-400">{filteredTransactions.length} transaction(s)</p>
            </div>
          </div>

          <div className="flex flex-col gap-3 xl:items-end">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Chercher..." className="w-full rounded-xl border border-[#e5e7eb] bg-white py-2.5 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30 xl:w-64" />
              </div>
              <button
                type="button"
                onClick={() => setShowFilters((current) => !current)}
                className={`inline-flex items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors ${showFilters || kindFilter !== 'all' || statusFilter !== 'all' ? 'border-navy bg-navy text-white' : 'border-[#e5e7eb] bg-white text-navy hover:bg-gray-50'}`}
              >
                <i className="ri-filter-3-line"></i>
                Filtre
              </button>
            </div>
            {showFilters ? (
              <div className="flex flex-col gap-2 xl:items-end">
                <div className="flex flex-wrap items-center gap-1 rounded-xl bg-[#f5f7fa] p-1">
                  {[
                    { key: 'all', label: 'Tous' },
                    { key: 'reservation', label: 'Réservations' },
                    { key: 'visit', label: 'Visites' },
                  ].map((item) => (
                    <button key={item.key} onClick={() => setKindFilter(item.key as typeof kindFilter)} className={`rounded-lg px-3 py-1.5 text-xs font-medium ${kindFilter === item.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                      {item.label}
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-1 rounded-xl bg-[#f5f7fa] p-1">
                  {[
                    { key: 'all', label: 'Tous' },
                    { key: 'completed', label: 'Paiement réussi' },
                    { key: 'pending', label: 'Paiement non validé' },
                    { key: 'failed', label: 'Échoué' },
                  ].map((item) => (
                    <button key={item.key} onClick={() => setStatusFilter(item.key as typeof statusFilter)} className={`rounded-lg px-3 py-1.5 text-xs font-medium ${statusFilter === item.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1360px]">
            <thead>
              <tr className="border-b border-[#f2f4f7] bg-[#fcfcfd]">
                {['Référence', 'Salle', 'Client', 'Type', 'Date réservation', 'Date transaction', 'Total', 'Commission FSN', 'DexPay (2%)', 'Paiement', 'Statut', 'Actions'].map((label) => (
                  <th key={label} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-400">{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredTransactions.map((transaction) => {
                const isVisit = transaction.creneau_id === 'visite_gratuite';
                const paymentStatus = transaction.status_paiement || transaction.statut;
                const fsnCommission = estimateFsnCommission(transaction);
                return (
                  <tr key={transaction.id} className="border-b border-[#f4f6f8] transition-colors hover:bg-[#fafbfd]">
                    <td className="px-4 py-4 text-xs font-semibold text-gold-dark">LOC-{transaction.id}</td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-semibold text-gray-900">{transaction.lieu_nom || 'Salle non renseignée'}</p>
                      <p className="text-xs text-gray-400">{transaction.lieu_ville || 'Ville non renseignée'}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-semibold text-gray-900">{transaction.nom}</p>
                      <p className="text-xs text-gray-400">{transaction.email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${isVisit ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'}`}>
                        {isVisit ? 'Visite' : 'Réservation'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-xs font-semibold text-gray-700">{formatReservationDate(transaction.date_reservation)}</p>
                      <p className="text-xs text-gray-400">{transaction.creneau_label}</p>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">{formatDate(transaction.created_at)}</td>
                    <td className="px-4 py-3 text-sm font-bold text-navy">{formatMoney(Number(transaction.montant ?? 0))}</td>
                    <td className="px-4 py-3 text-sm font-bold text-emerald-600">{formatMoney(fsnCommission)}</td>
                    <td className="px-4 py-3 text-sm font-bold text-blue-700">{formatMoney(estimateDexpayFee(Number(transaction.montant ?? 0)))}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{paymentMethodLabel(transaction.payment_method)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${statusClass(paymentStatus)}`}>
                        {statusLabel(paymentStatus)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => setSelectedTransaction(transaction)} className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-navy/10 hover:text-navy" title="Voir le détail">
                          <i className="ri-eye-line text-base"></i>
                        </button>
                        <button onClick={() => void handleDelete(transaction)} className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-red-500 transition-colors hover:bg-red-50 hover:text-red-700" title="Supprimer la transaction" aria-label={`Supprimer LOC-${transaction.id}`}>
                          <i className="ri-delete-bin-line text-base"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {filteredTransactions.length === 0 ? (
            <div className="px-6 py-10 text-center text-sm text-gray-400">Aucune transaction de salle trouvée.</div>
          ) : null}
        </div>
      </div>

      {selectedTransaction ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40 px-4 py-6">
          <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-gray-100 px-6 py-5">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-gold-dark">Détail paiement salle</p>
                <h3 className="mt-1 text-xl font-bold text-navy">LOC-{selectedTransaction.id}</h3>
              </div>
              <button onClick={() => setSelectedTransaction(null)} className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="max-h-[72vh] overflow-y-auto px-6 py-5">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {[
                  ['Salle', selectedTransaction.lieu_nom || 'Non renseignée'],
                  ['Ville', selectedTransaction.lieu_ville || 'Non renseignée'],
                  ['Client', selectedTransaction.nom],
                  ['Email client', selectedTransaction.email],
                  ['Téléphone client', selectedTransaction.telephone || 'Non renseigné'],
                  ['Adresse client', selectedTransaction.adresse || 'Non renseignée'],
                  ['Date réservation', formatReservationDate(selectedTransaction.date_reservation)],
                  ['Créneau', selectedTransaction.creneau_label],
                  ['Montant payé', formatMoney(Number(selectedTransaction.montant ?? 0))],
                  ['Commission FSN', formatMoney(estimateFsnCommission(selectedTransaction))],
                  ['Frais DexPay (2%)', formatMoney(estimateDexpayFee(Number(selectedTransaction.montant ?? 0)))],
                  ['Moyen de paiement', paymentMethodLabel(selectedTransaction.payment_method)],
                  ['Statut paiement', statusLabel(selectedTransaction.status_paiement || selectedTransaction.statut)],
                  ['Statut demande', statusLabel(selectedTransaction.statut)],
                  ['Transaction DexPay', selectedTransaction.transaction_id || 'Non renseignée'],
                  ['Créée le', formatDate(selectedTransaction.created_at)],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-xl border border-gray-100 px-4 py-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">{label}</p>
                    <p className="mt-1 text-sm font-semibold text-gray-800">{value}</p>
                  </div>
                ))}
              </div>
              {selectedTransaction.message ? (
                <div className="mt-4 rounded-xl border border-gray-100 px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Message</p>
                  <p className="mt-1 whitespace-pre-line text-sm text-gray-700">{selectedTransaction.message}</p>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
